import React, { useEffect, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import { generateClient } from 'aws-amplify/api';
import { ContentShare, DocumentStatus } from '@alucio/aws-beacon-amplify/src/models';
import { contentShareByContentID } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { DNABox, DNAButton, DNAText, DNADivider, Iffy, DNAContextMenu, util } from '@alucio/lux-ui/src';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { ContentShareModal, DNAChip, DNADocumentStatusChip, DNACommonConfirmation, ShareVariantOptions } from 'src/components/DNA';
import { ContentShareORM, HubORM } from 'src/types/orms';
import { useDispatch, store } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { contentShareActions } from 'src/state/redux/slice/contentShare';
import { useContentShareListByContentIdSortedByCreatedAt } from 'src/state/redux/selector/contentShare';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import { useHubRHFromState } from 'src/screens/Hubs/EditHub/useHubRHFormStateProvider';
import { TabConfig} from 'src/state/context/Hubs/HubsStateProvider';
import { useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { CustomFieldUsage, CustomValues, CustomFieldDefinition } from '@alucio/aws-beacon-amplify/src/models';
import * as logger from 'src/utils/logger';

type SharedWithProps = {
  hubORM: HubORM
  tabConfig: TabConfig,
}

/**
 * Finds the recipient field from customValues by checking reportingName for "email" or "recipient",
 * otherwise returns the first field as fallback
 */
const findRecipientField = (
  customValues: CustomValues[] | undefined,
  customFields: CustomFieldDefinition[] | undefined
): CustomValues | undefined => {
  if (!customValues?.length || !customFields?.length) {
    return customValues?.[0]
  }
    
  const emailField = customValues.find(cv => {
    const field = customFields.find(f => f.id === cv.fieldId)
    return field?.reportingName?.toLowerCase().includes('email')
  })
  if (emailField) return emailField
  
  const recipientField = customValues.find(cv => {
    const field = customFields.find(f => f.id === cv.fieldId)
    return field?.reportingName?.toLowerCase().includes('recipient')
  })
  if (recipientField) return recipientField
  
  return customValues[0]
}

const styles = StyleSheet.create({
  sharedWithMeContent: {
    paddingHorizontal: 12,
    backgroundColor: colors['color-text-white'],
  },
  sideBarHeaderDesktop: {
    paddingTop: 28,
    paddingBottom: 18,
  },
  sideBarHeaderTablet: {
    paddingTop: 12,
    paddingBottom: 18,
  },
  addRecipientBtnContainer: {
    marginBottom: 20,
  },
  shareListContainer: {
    borderColor: colors['color-gray-100'],
    borderWidth: 1,
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
    width: 272,
  },
  emptyContainer: {
    width: 272,
    paddingHorizontal: 12,
  },
  emptyContainerSubText: {
    textAlign: 'center',
    lineHeight: 24,
  },
  fullWidth: {
    width: '100%',
  },
});

const SharedWith: React.FC<SharedWithProps> = (props) => {
  const { hubORM, tabConfig } = props
  const { deviceMode, isOnline } = useAppSettings()
  const isTablet = deviceMode === 'tablet'
  
  // returns a list of contentShare for the current hub by hub id
  const contentShareORMs = useContentShareListByContentIdSortedByCreatedAt(hubORM.model.id)
  const dispatch = useDispatch()
  const { rhForm, setIsSaving } = useHubRHFromState()
  const customFields = useTenantCustomFields({ usages: { internalUsages: [CustomFieldUsage.HUB] } })
  
  const {
    openFormInvalidWarningModal,
    setErrorMessages,
    handleSaveNCallback,
    toggleRightSidebar,
  } = useHubsState()
  const { handleSubmit } = rhForm

  const recipientFields = useMemo(
    () => {
      return contentShareORMs?.reduce<Record<string, CustomValues | undefined>>(
        (acc, contentShareORM) => {
          acc[contentShareORM.model.id] = findRecipientField(contentShareORM.model.customValues, customFields);
          return acc;
        },
        {}
      );
    },
    [contentShareORMs, customFields]
  );

  const getExistingContentShareORM = (contentShareId: string) => {
    return contentShareORMs.find(contentShareORM => contentShareORM.model.id === contentShareId)
  }

  const handleOpenShareModalToCopy = (existingContentShareORM: ContentShareORM, hubTitle: string) => {
    if (hubORM) {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        backdropVisible: true,
        component: (modalProps) => (
          <ContentShareModal
            entityShareIds={[hubORM.model.id]}
            {...modalProps}
            variant={ShareVariantOptions.HUB_SHARE}
            existingContentShareORM={existingContentShareORM}
            hubTitle={hubTitle}
          />
        ),
      }))
    }
  }
  
  useEffect(() => {
    const getData = async () => {
      const appsyncClient = generateClient();
      const { data } = await appsyncClient.graphql({
        query: contentShareByContentID,
        variables: { contentId: hubORM.model.id }
      })

      if (data?.contentShareByContentID?.items) {
        // @ts-expect-error - [AMPLIFY-TODO] - We should not rely on the GQL response but rather the DataStore record
        store.dispatch(contentShareActions.upsert(data.contentShareByContentID.items))
      }
    }
    // TODO better error handling
    getData().catch(err => console.log('error getting data: ', err))
  }, [])

  const handleAddRecipient = () => {
    logger.hub.sharing.debug(`Opening share modal for hub: ${hubORM.model.id}`)
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: false,
      backdropVisible: true,
      component: (modalProps) => (
        <ContentShareModal
          entityShareIds={[hubORM.model.id]}
          {...modalProps}
          variant={ShareVariantOptions.HUB_SHARE}
        />
      ),
    }))
  }

  const saveNCallbackWrapper = (data: any, callback: () => void) => {
    setIsSaving(true)
    try {
      handleSaveNCallback(data, callback)
      setTimeout(() => {
        setIsSaving(false)  
      }, 1000)
    } catch (e) {
      setErrorMessages(pre => [...pre, 'An error has occured, please try again. If this issue persists, please contact support'])
      console.error('Hub saving faild', e)
    }
  }

  const getExpirationOrStatusText = (expirationDate: string, revoked?: boolean) => {
    const hasExpired = isAfter(Date.now(), Date.parse(expirationDate))
    if (revoked) return <DNADocumentStatusChip documentStatus={DocumentStatus.REVOKED} />
    else if (hasExpired) {
      return (
        <DNAChip.Beacon status="danger" children={`EXPIRED ON ${format(Date.parse(expirationDate), 'L/d/yy')}`} />
      )
    } else {
      return (
        <DNAText status="primary">
          {`Expires on ${format(Date.parse(expirationDate), 'L/d/yy')}`}
        </DNAText>
      )
    }
  }

  const handleRevoke = async (contentShare: ContentShare) => {
    dispatch(
      DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: () => (<DNACommonConfirmation
          status="danger"
          cancelText="Cancel"
          confirmActionText="Revoke"
          onConfirmAction={() => dispatch(contentShareActions.revokeContentShare({ contentShare }))}
          title="Revoke access to hub?"
          descriptionText={`This will revoke access to this hub for this generated link. \n\n This action cannot be undone.`}
        />),
    }));
  }

  return (
    <DNABox as={ScrollView} style={styles.sharedWithMeContent} fill childFill={2} appearance="col">
      <DNABox
        testID="side-bar-header-text"
        appearance="col"
        spacing="md"
        style={util.mergeStyles(
          undefined,
          [styles.sideBarHeaderDesktop, !isTablet],
          [styles.sideBarHeaderTablet, isTablet],
        )}
      >
        <DNABox fill appearance="row" spacing="between" alignY="center">
          <DNAText testID="side-bar-text" h4>
            {`${tabConfig.label}` + (isOnline ? ` (${contentShareORMs.length})` : '')}
          </DNAText>
          <Iffy is={isTablet}>
            <DNAButton
              appearance="ghost"
              status="subtle"
              iconLeft="x"
              rounded
              onPress={toggleRightSidebar}
            />
          </Iffy>
        </DNABox>
        <DNADivider />
      </DNABox>
      {/* ADD RECIPIENT BUTTON */}
      <DNABox style={styles.addRecipientBtnContainer}>
        <DNAButton
          testID="edit-hub-add-recipient-btn"
          appearance="outline"
          status="tertiary"
          size="lg"
          iconLeft="plus"
          stretch
          onPress={handleSubmit((data) => saveNCallbackWrapper(data, handleAddRecipient), openFormInvalidWarningModal)}
          disabled={!isOnline}
          >
          Add recipient
        </DNAButton>

      </DNABox>
      <DNABox fill>
        <Iffy is={!isOnline}>
          <DNABox
            appearance="col"
            alignX="center"
            alignY="center"
            spacing="md"
            fill
            style={util.mergeStyles(
              undefined,
              styles.emptyContainer,
              [styles.fullWidth, isTablet],
            )}
          >
            <DNAText status="subtle" numberOfLines={3} style={styles.emptyContainerSubText}>
              Please connect to the internet to see content
            </DNAText>
          </DNABox>
        </Iffy>
        <Iffy is={!contentShareORMs?.length && isOnline}>
          <DNABox
            appearance="col"
            alignX="center"
            alignY="center"
            spacing="md"
            fill
            style={util.mergeStyles(
              undefined,
              styles.emptyContainer,
              [styles.fullWidth, isTablet],
            )}
          >
            <DNAText status="subtle" bold>
              Share hub by adding recipients
            </DNAText>
            <DNAText status="subtle" numberOfLines={3} style={styles.emptyContainerSubText}>
              Each recipient will have a unique share link that will allow you to track their activity.
            </DNAText>
          </DNABox>
          </Iffy>
        <Iffy is={contentShareORMs?.length && isOnline}>
          <DNABox fill appearance="col">
          {contentShareORMs?.map(contentShareORM => {
            const handleReshare = () => {
              const existingContentShareORM = getExistingContentShareORM(contentShareORM.model.id)
              if (existingContentShareORM) handleOpenShareModalToCopy(existingContentShareORM, hubORM.model.name || '')
            }
            const recipientField = recipientFields?.[contentShareORM.model.id]
            return (
              <DNABox
                key={contentShareORM.model.id}
                appearance="col"
                style={util.mergeStyles(
                  undefined,
                  styles.shareListContainer,
                  [styles.fullWidth, isTablet],
                )}
                spacing="sm"
                keepSpacingLastItem
              >
                <DNABox>
                  <DNAText status="primary" b1 bold numberOfLines={1}>
                    {recipientField?.values}
                  </DNAText>
                </DNABox>
                <DNABox fill appearance="row" spacing="between">
                  <DNABox appearance="col" spacing="sm">
                    <DNAText status="tertiary">Link generated on {format(Date.parse(contentShareORM.model.createdAt), 'L/d/yy')}</DNAText>
                    <DNABox childFill>
                      {getExpirationOrStatusText(contentShareORM.model.expiresAt, contentShareORM.model.revoked)}
                    </DNABox>
                  </DNABox>
                  <Iffy is={!contentShareORM.model.revoked && !isAfter(Date.now(), Date.parse(contentShareORM.model.expiresAt))}>
                    <DNABox alignY="center">
                      <DNAButton
                        testID="share-icon"
                        appearance="ghost"
                        status="subtle"
                        iconLeft="share-fat"
                        onPress={handleSubmit((data) => saveNCallbackWrapper(data, handleReshare), openFormInvalidWarningModal)}
                      />
                      <DNAContextMenu>
                        <DNAContextMenu.Anchor>
                          <DNAButton
                            appearance="ghost"
                            status="subtle"
                            iconRight="dots-three-vertical-bold"
                          />
                        </DNAContextMenu.Anchor>
                        <DNAContextMenu.Items>
                          <DNAContextMenu.Item
                            title="Revoke"
                            icon='arrow-u-up-left'
                            onPress={() => handleRevoke(contentShareORM.model)}
                          />
                        </DNAContextMenu.Items>
                      </DNAContextMenu>
                    </DNABox>
                  </Iffy>
                </DNABox>
              </DNABox>
            )
          })}
          </DNABox>
        </Iffy>
      </DNABox>
    </DNABox>
  )
}

export default SharedWith
